<template>
  <div
    class="raw"
    id="rawJson"
    contenteditable
    @input="onInput($event)"
    @keydown="tabSpace($event)"
    @blur="onBlurFormatting()"
  >{{ content }}</div>
</template>

<script>
import { Bus } from "../main.js";
import { ALERT_TYPE, MESSAGES } from '../mixins/constant-mixin.js';

export default {
  name: "RawJSON",
  props: {
    json: {
      type: Object,
    },
    root: {
      type: String,
    }
  },
  data() {
    return {
      rawJson: JSON.stringify(this.json, null, 2),
      content: "",
    };
  },
  created() {
    this.content = structuredClone(this.rawJson);
    if (this.content === "null") {
      this.content = null;
    }
  },
  mounted() {
    // emit from Home
    Bus.$on("saveRaw", () => {
      this.$emit("saveRaw", this.rawJson);
    });

    // emit from Home
    Bus.$on("loadJSON", () => {
      this.$nextTick(() => {
        this.content = this.json? JSON.stringify(this.json, null, 2): null;
        this.rawJson = structuredClone(this.content);
        document.getElementById("rawJson").innerText = this.content;
        if (this.json) {
          this.setColor();
        }
      })
    })

    if (this.json) {
      this.setColor();
    }
  },
  beforeDestroy() {
    Bus.$off("saveRaw");
    Bus.$off("loadJSON");
  },
  methods: {
    onInput: function (event) {
      event.preventDefault();
      this.rawJson = event.target.innerText;
    },
    tabSpace: function (event) {
      const TAB_KEYCODE = 9;
      if (event.keyCode === TAB_KEYCODE) {
        event.preventDefault();
        const sel = document.getSelection();
        const range = sel.getRangeAt(0);

        const tabNodeValue = "\u0009";
        const tabNode = document.createTextNode(tabNodeValue);

        range.insertNode(tabNode);
        range.setStartAfter(tabNode);
        range.setEndAfter(tabNode);
      }
    },
    replaceNbsp: function () {
      // in some case when copy from code editor,
      // the white space is non-breaking space(nbsp) instead of space
      // the JSON.parse cannot read nbsp and will cause error
      const nbsp = String.fromCharCode(160);
      this.rawJson = this.rawJson.replaceAll(nbsp, " ");
    },
    onBlurFormatting: function () {
      if (!this.rawJson) {
        return;
      }
      this.replaceNbsp();
        try {
          if (this.rawJson === "null") {
            this.content = null;
            document.getElementById("rawJson").innerText = null;
            return;
          } else {
            this.content = JSON.stringify(JSON.parse(this.rawJson), null, 2);
            document.getElementById("rawJson").innerText = this.content;
          }
        } catch (e) {
          Bus.$emit("openAlert", MESSAGES.INVALID_JSON, ALERT_TYPE.ERROR);
          return;
        }
        this.setColor();
    },
    // call this function instead of calling one by one
    // to prevent the error caused by wrong function calling sequence(setFontColor() must be called first)
    setColor: function () {
      this.setFontColor();
      this.setBracketColor();
    },
    // run when the component is mounted OR when the save button is clicked
    setBracketColor: function () {
      // the current level of the curley braces
      // to set the class name
      let level = 1;

      // it copy the value of the innerHTML instead of referencing
      let rawJsonHTML = document.getElementById("rawJson").innerHTML;

      // remove all the character except "{" and "}"
      // to determine the next symbol in the loop
      let braces = this.content.replace(/([^{}])/g, "");

      // the index of the target curley braces
      let lastIndex = 0;

      /* start from the end of the innerHTML, so that the <span> tag added will not affect the index in the next index search
       ** the indentation logic of "{" and "}" should be reversed because of iterating from the end
       ****|-> the "}" mean the start of the current level, and the symbol in next iteration is at a larger level (deeper indentation)
       ****|-> the "{" mean the end of the current level, and the symbol in next iteration is at a smaller level
       ** change the level and replace the "{" with a "<span class="bracket-level"> { </span>"
       ** find the next index start from the (lastIndex - 1) to avoid picking the same symbol
       */
      for (let i = braces.length - 1; i >= 0; i--) {
        const element = braces[i];
        // if the next symbol is "{"
        if (element === "{") {
          lastIndex = rawJsonHTML.lastIndexOf("{", lastIndex - 1);
          level -= 1;
          rawJsonHTML =
            rawJsonHTML.substr(0, lastIndex) +
            `<span class="bracket-${level}">{</span>` +
            rawJsonHTML.substr(lastIndex + 1);
        }
        // if the next symbol is "}"
        else {
          if (i == braces.length - 1) {
            // lastIndexOf("}", lastIndex - 1) cannot run in the first iteration
            // it will skip the first "}", and the lastIndex is invalid
            lastIndex = rawJsonHTML.lastIndexOf("}");
          } else {
            lastIndex = rawJsonHTML.lastIndexOf("}", lastIndex - 1);
          }
          rawJsonHTML =
            rawJsonHTML.substr(0, lastIndex) +
            `<span class="bracket-${level}">}</span>` +
            rawJsonHTML.substr(lastIndex + 1);
          level += 1;
        }
      }
      document.getElementById("rawJson").innerHTML = rawJsonHTML;
    },

    // change the font color of the key and value based on the type
    // it must be called before setBracketColor, else the regex will not work properly
    // because the div content after the setBracketColor() is no longer a valid JSON string(having <span> tag)
    setFontColor: function () {
      let rawJsonHTML = document.getElementById("rawJson").innerHTML;
      rawJsonHTML = rawJsonHTML.replace(
        /("(\\u[a-zA-Z0-9]{4}|\\[^u]|[^\\"])*"(\s*:)?|\b(true|false|null)\b|-?\d+(?:\.\d*)?(?:[eE][+\-]?\d+)?)/g,
        (match) => this.matchRegExp(match)
      );
      document.getElementById("rawJson").innerHTML = rawJsonHTML;
    },
    // function for the setFontColor()
    matchRegExp: function (string) {
      const regexMap = {
        "^\".*:$": "key",
        "^\".*[^:]$": "string",
        "^-?\\d*\\.?\\d*$": "number",
        "true|false": "boolean",
        "null": "null",
      }
      let cls;
      Object.keys(regexMap).forEach(key => {
        const regex = new RegExp(key)
        if (regex.test(string)) {
          cls = regexMap[key];
        }
      })
      return `<span class="json-${cls}">${string}</span>`;
    },
  },
};
</script>

<style scoped>
.raw {
  margin: 10px 0;
  white-space: pre-wrap;
  height: 60vh;
  overflow-y: auto;
  border: 1px solid;
  padding: 5px;
  font-size: 14px;
}
</style>

<style>
.bracket-1 {
  color: violet;
}

.bracket-2 {
  color: darkturquoise;
}

.bracket-3 {
  color: deeppink;
}

.bracket-4 {
  color: blue;
}

.json-key {
  color: brown;
}

.json-string {
  color: green;
}

.json-number {
  color: darkorange;
}

.json-boolean {
  color: blue;
}

textarea {
  width: 100%;
  height: 70vh;
}

.json-null {
  color: magenta;
}
</style>